import { useState } from "react"
import {
  useUpdateActivity,
  useDeleteActivity,
  useCreateActivitySection,
  useReorderActivitySections,
} from "@/lib/queries"
import type { AdminWorkflowActivity, AdminSection } from "@/lib/types"
import { SectionEditor } from "./SectionEditor"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
} from "lucide-react"

interface ActivityEditorProps {
  activity: AdminWorkflowActivity
  isFirst: boolean
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onUpdate: (activity: AdminWorkflowActivity) => void
  onDelete: () => void
  canEdit?: boolean
}

export function ActivityEditor({
  activity,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onUpdate,
  onDelete,
  canEdit = true,
}: ActivityEditorProps) {
  const [open, setOpen] = useState(true)
  const [name, setName] = useState(activity.name)
  const [sections, setSections] = useState<AdminSection[]>(activity.sections)

  const updateActivity = useUpdateActivity()
  const deleteActivity = useDeleteActivity()
  const createSection = useCreateActivitySection()
  const reorderSections = useReorderActivitySections()

  const handleNameBlur = async () => {
    if (name.trim() === "" || name === activity.name) {
      setName(activity.name)
      return
    }

    try {
      const updated = await updateActivity.mutateAsync({
        id: activity.id,
        name,
      })
      onUpdate({ ...activity, name: updated.name, sections })
    } catch (error) {
      console.error("Failed to update activity name:", error)
      setName(activity.name)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteActivity.mutateAsync(activity.id)
      onDelete()
    } catch (error) {
      console.error("Failed to delete activity:", error)
    }
  }

  const handleAddSection = async () => {
    try {
      const newSection = await createSection.mutateAsync({
        activityId: activity.id,
        heading: "New Section",
        body_html: "",
      })
      const updated = [...sections, newSection]
      setSections(updated)
      onUpdate({ ...activity, sections: updated })
    } catch (error) {
      console.error("Failed to add section:", error)
    }
  }

  const handleSectionUpdate = (update: {
    id: number
    heading: string
    body_html: string
    lock_version: number
  }) => {
    const updated = sections.map((s) =>
      s.id === update.id ? { ...s, ...update } : s
    )
    setSections(updated)
    onUpdate({ ...activity, sections: updated })
  }

  const handleSectionDelete = (sectionId: number) => {
    const updated = sections.filter((s) => s.id !== sectionId)
    setSections(updated)
    onUpdate({ ...activity, sections: updated })
  }

  const handleMoveSection = async (
    index: number,
    direction: "up" | "down"
  ) => {
    const newSections = [...sections]
    const targetIndex = direction === "up" ? index - 1 : index + 1

    if (targetIndex < 0 || targetIndex >= newSections.length) return

    ;[newSections[index], newSections[targetIndex]] = [
      newSections[targetIndex],
      newSections[index],
    ]

    const newOrder = newSections.map((s) => s.id)

    try {
      const reordered = await reorderSections.mutateAsync({
        activityId: activity.id,
        section_ids: newOrder,
      })
      setSections(reordered)
      onUpdate({ ...activity, sections: reordered })
    } catch (error) {
      console.error("Failed to reorder sections:", error)
    }
  }

  return (
    <div className="border rounded-lg bg-card">
      <div className="flex items-center gap-2 p-4">
        <Collapsible open={open} onOpenChange={setOpen} className="flex-1">
          <div className="flex items-center gap-2">
            <CollapsibleTrigger className="shrink-0">
              <ChevronRight
                className={`h-5 w-5 transition-transform ${open ? "rotate-90" : ""}`}
              />
            </CollapsibleTrigger>
            {canEdit ? (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleNameBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleNameBlur()
                    ;(e.target as HTMLInputElement).blur()
                  }
                }}
                className="font-semibold text-lg"
              />
            ) : (
              <span className="font-semibold text-lg">{name}</span>
            )}
          </div>
        </Collapsible>

        {canEdit && (
          <div className="flex gap-1 shrink-0">
            <Button
              size="icon"
              variant="ghost"
              onClick={onMoveUp}
              disabled={isFirst}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={onMoveDown}
              disabled={isLast}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="ghost">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Activity</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete "{activity.name}" and all its
                    sections? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div className="ml-6 space-y-4">
            {sections.map((section, index) => (
              <SectionEditor
                key={section.id}
                section={section}
                sectionKey={section.section_key}
                isWorkflow={true}
                isFirst={index === 0}
                isLast={index === sections.length - 1}
                onMoveUp={() => handleMoveSection(index, "up")}
                onMoveDown={() => handleMoveSection(index, "down")}
                onUpdate={handleSectionUpdate}
                onDelete={() => handleSectionDelete(section.id)}
                canEdit={canEdit}
              />
            ))}
          </div>

          {canEdit && (
            <Button
              variant="outline"
              onClick={handleAddSection}
              className="w-full ml-6"
              style={{ width: "calc(100% - 1.5rem)" }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
